import {
    Action,
    combineReducers,
    configureStore,
    createListenerMiddleware,
    Middleware,
    ThunkAction
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { apiSlice } from "../api/apiSlice";
import * as AppConfigStore from "./AppConfigStore";
import * as AuctionStore from "./AuctionStore";
import * as DebugStore from "./DebugStore";
import * as ParcelBidStore from "./ParcelBidStore";
import * as ParcelStore from "./ParcelStore";
import { addProcessBidSpanListener } from "./processBidSpanListener";
import { addResetApiStateListener } from "./resetApiStateListener";
import * as TimerStore from "./TimerStore";
import * as UserStore from "./UserStore";

const rootReducer = combineReducers({
    appConfigStoreState: AppConfigStore.reducer,
    auctionStoreState: AuctionStore.reducer,
    debugStoreState: DebugStore.reducer,
    parcelBidStoreState: ParcelBidStore.reducer,
    parcelStoreState: ParcelStore.reducer,
    timerStoreState: TimerStore.reducer,
    userStoreState: UserStore.reducer,
    // New
    [apiSlice.reducerPath]: apiSlice.reducer
});

export type IAppState = ReturnType<typeof rootReducer>;

export function setupStore(preloadedState?: Partial<IAppState>) {
    const listenerMiddleware = createListenerMiddleware();
    const startListening =
        listenerMiddleware.startListening.withTypes<IAppState>();
    addProcessBidSpanListener(startListening);
    addResetApiStateListener(startListening);

    const store = configureStore({
        reducer: rootReducer,
        preloadedState,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware()
                // Typed as a plain middleware: its `addListener` dispatch
                // overload breaks `connect`'s `Dispatch` typings, and listeners
                // are only added here.
                .prepend(listenerMiddleware.middleware as Middleware)
                .concat(apiSlice.middleware)
    });

    setupListeners(store.dispatch);

    return store;
}

export const store = setupStore();

export type IAppStore = ReturnType<typeof setupStore>;
export type IAppDispatch = IAppStore["dispatch"];
export type IAppThunkAction<ThunkReturnType = unknown> = ThunkAction<
    ThunkReturnType,
    IAppState,
    void,
    Action
>;
